// NumericKeypad.tsx
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  KeyboardEvent,
} from "react";
import {
  Paper,
  Text,
  Button,
  Group,
  SimpleGrid,
  SegmentedControl,
  Box,
  rem,
} from "@mantine/core";
import { IconBackspace } from "@tabler/icons-react";
import { currencyConfig, formatFiat } from "../../helpers/formatNumbers";
import { CurrencySnapshot } from "../../helpers/types";
import { SquareButton } from "./squareButton";

/* ──────────────────────────────  keypad  ───────────────────────────────── */

export interface NumericKeypadProps {
  currencyCode: string;
  currencySnapshot: CurrencySnapshot;
  initialMode?: "fiat" | "sats";
  valueSats?: number;
  onChangeSats?: (sats: number) => void;
  onOk?: (sats: number) => void; // show OK only when supplied
  minSats?: number;
  maxSats?: number;
  error?: string;
  readOnly?: boolean;
}

export const NumericKeypad: React.FC<NumericKeypadProps> = ({
  currencyCode,
  currencySnapshot,
  initialMode = "fiat",
  valueSats,
  onChangeSats,
  onOk,
  minSats = 0,
  maxSats = Number.MAX_SAFE_INTEGER,
  error,
  readOnly = false,
}) => {
  /* ── state ──────────────────────────────────────────────────────────── */
  const locale = navigator.language;
  const [mode, setMode] = useState<"fiat" | "sats">(initialMode);
  const [raw, setRaw] = useState("");
  const lastSentRef = useRef<number | null>(null);

  /* ── helpers ────────────────────────────────────────────────────────── */
  const rate = currencySnapshot[currencyCode.toLowerCase()] ?? 0;
  const { symbol, precision } = currencyConfig[currencyCode.toUpperCase()] ?? {
    symbol: "",
    precision: 2,
  };
  const fiatToSats = (f: number) => Math.round((f * 100_000_000) / rate);
  const satsToFiat = (s: number) => (s / 100_000_000) * rate;
  const clamp = (s: number) => Math.min(maxSats, Math.max(minSats, s));

  const pushUpstream = (r: string, m = mode) => {
    if (!onChangeSats) return;
    let sats = 0;
    if (m === "sats") {
      sats = Number(r || "0");
    } else {
      const parsed = parseFloat(r || "0");
      sats = parsed === 0 ? 0 : fiatToSats(parsed);
    }
    const val = sats === 0 ? 0 : sats; // Don't clamp here, let parent handle validation
    if (val !== lastSentRef.current || val === 0) {
      lastSentRef.current = val;
      onChangeSats(val);
    }
  };

  /* ── sync external value ────────────────────────────────────────────── */
  useEffect(() => {
    if (valueSats === undefined) return;
    if (valueSats === lastSentRef.current) return;
    lastSentRef.current = valueSats;
    const next =
      mode === "sats"
        ? valueSats.toString()
        : satsToFiat(valueSats).toFixed(precision);
    setRaw(next === "0" ? "" : next);
  }, [valueSats, mode, precision]);

  /* ── send upstream on local edits ───────────────────────────────────── */
  useEffect(() => {
    if (raw === "") {
      onChangeSats?.(0);
      return;
    }
    pushUpstream(raw, mode);
  }, [raw, mode]);

  /* ── digit / point / editing helpers ────────────────────────────────── */
  const addDigit = (d: string) =>
    !readOnly &&
    setRaw((prev) => {
      if (/^0(\.0*)?$/.test(prev)) return d;
      if (mode === "fiat" && prev.includes(".")) {
        const [, dec] = prev.split(".");
        if (dec.length >= precision) return prev;
      }
      return prev + d;
    });

  const addPoint = () =>
    !readOnly &&
    mode === "fiat" &&
    !raw.includes(".") &&
    setRaw((prev) => (prev === "" ? "0." : prev + "."));

  const backspace = () => !readOnly && setRaw((v) => v.slice(0, -1));
  const clear = () => !readOnly && setRaw("");

  const changeMode = (m: "fiat" | "sats") => {
    if (readOnly || m === mode) return;
    setRaw((prev) =>
      prev === ""
        ? prev
        : m === "fiat"
        ? satsToFiat(Number(prev)).toFixed(precision)
        : fiatToSats(parseFloat(prev)).toString()
    );
    setMode(m);
  };

  const confirmViaEnter = () => {
    if (!onOk) return;
    const sats =
      mode === "sats" ? Number(raw || "0") : fiatToSats(parseFloat(raw || "0"));
    onOk(clamp(sats));
  };

  /* ── keyboard support ──────────────────────────────────────────────── */
  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const k = e.key;
    if (/^[0-9]$/.test(k)) {
      e.preventDefault();
      addDigit(k);
    } else if (k === "." || k === ",") {
      e.preventDefault();
      addPoint();
    } else if (k === "Backspace") {
      e.preventDefault();
      backspace();
    } else if (k === "Enter") {
      e.preventDefault();
      confirmViaEnter();
    } else if (k === "Escape") {
      e.preventDefault();
      clear();
    }
  };

  /* ── formatted outputs ─────────────────────────────────────────────── */
  const main = useMemo(() => {
    if (raw === "") return "0";
    if (mode === "sats")
      return Number(raw).toLocaleString(locale, { maximumFractionDigits: 0 });
    const [intPart, dec] = raw.split(".");
    return (
      symbol +
      Number(intPart || "0").toLocaleString(locale) +
      (dec ? `.${dec}` : "")
    );
  }, [raw, mode, symbol, locale]);

  const secondary = useMemo(() => {
    if (raw === "") return "";
    if (mode === "sats")
      return formatFiat({
        satoshis: Number(raw),
        currency: currencyCode,
        currencySnapshot,
        locale,
      });
    const sats = fiatToSats(parseFloat(raw));
    return `${sats.toLocaleString(locale)} sats`;
  }, [raw, mode, locale, currencyCode, currencySnapshot]);

  /* ── render ────────────────────────────────────────────────────────── */
  return (
    <Paper
      tabIndex={0}
      data-autofocus
      onKeyDown={handleKey}
      shadow="md"
      p="md"
      radius="md"
      style={{
        width: "100%",
        maxWidth: rem(340), // keeps keys from ballooning on large screens
        margin: "0 auto",
        opacity: readOnly ? 0.5 : 1,
      }}
    >
      <Group justify="center" mb="sm">
        <SegmentedControl
          value={mode}
          onChange={(v) => changeMode(v as "fiat" | "sats")}
          data={[
            { value: "fiat", label: "Fiat" },
            { value: "sats", label: "Sats" },
          ]}
          disabled={readOnly}
        />
      </Group>

      {/* <Paper withBorder radius="sm" p="sm" mb="md">
        <Text size="xl" fw={500} ta="right">
          {main}
        </Text>

        <Box h={rem(18)}>
          {secondary && (
            <Text size="sm" c="dimmed" ta="right">
              {secondary}
            </Text>
          )}
        </Box>

        <Box h={rem(18)}>
          {error && (
            <Text size="xs" c="red" ta="right">
              {error}
            </Text>
          )}
        </Box>
      </Paper> */}

      <Box pos="relative" mb="md">
        <Paper withBorder radius="sm" p="sm">
          <Text size="xl" fw={500} ta="right">
            {main}
          </Text>

          <Box h={rem(18)}>
            {secondary && (
              <Text size="sm" c="dimmed" ta="right">
                {secondary}
              </Text>
            )}
          </Box>

          <Box h={rem(18)}>
            {error && (
              <Text size="xs" c="red" ta="right">
                {error}
              </Text>
            )}
          </Box>
        </Paper>

        {!readOnly && (
          <Button
            variant="subtle"
            size="xs"
            color="gray.6"
            onClick={clear}
            style={{
              position: "absolute",
              top: rem(4),
              left: rem(6),
              padding: "0 6px",
              fontSize: rem(10),
              fontWeight: 400,
              opacity: 0.6,
              lineHeight: 1.1,
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      <SimpleGrid cols={3} spacing="xs" mb="sm">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((n) => (
          <SquareButton key={n} onClick={() => addDigit(n)} disabled={readOnly}>
            {n}
          </SquareButton>
        ))}
        <SquareButton onClick={addPoint} disabled={readOnly || mode === "sats"}>
          .
        </SquareButton>
        <SquareButton onClick={() => addDigit("0")} disabled={readOnly}>
          0
        </SquareButton>
        <SquareButton
          onClick={backspace}
          aria-label="Backspace"
          disabled={readOnly}
        >
          <IconBackspace stroke={1.5} style={{ width: "60%", height: "60%" }} />
        </SquareButton>
      </SimpleGrid>

      <Group justify="center" grow>
        {onOk && (
          <Button
            size="lg"
            onClick={() => {
              const sats =
                mode === "sats"
                  ? Number(raw || "0")
                  : fiatToSats(parseFloat(raw || "0"));
              onOk(clamp(sats));
            }}
            disabled={readOnly || raw === ""}
          >
            OK
          </Button>
        )}
      </Group>
    </Paper>
  );
};
